import { Helmet } from 'react-helmet-async';

const DEFAULT_SEO = {
  siteName: 'Axeris Labs',
  siteUrl: 'https://www.axeris-labs.fr',
  defaultTitle: 'Axeris Labs | Création de sites web sur-mesure',
  defaultDescription: 'Création de sites web professionnels, e-commerce et applications web sur-mesure. Basé à Bourg-en-Bresse, j\'interviens dans l\'Ain, Lyon, Savoie et Haute-Savoie. Devis gratuit.',
  defaultImage: '/favicon.png',
  twitterHandle: '',
  locale: 'fr_FR',
};

export function SEO({ 
  title, 
  description, 
  path = '', 
  image,
  noIndex = false,
  structuredData = null 
}) {
  const pageTitle = title 
    ? `${title} | ${DEFAULT_SEO.siteName}` 
    : DEFAULT_SEO.defaultTitle;
  
  const pageDescription = description || DEFAULT_SEO.defaultDescription;
  const pageUrl = `${DEFAULT_SEO.siteUrl}${path}`;
  const pageImage = image || `${DEFAULT_SEO.siteUrl}${DEFAULT_SEO.defaultImage}`;

  return (
    <Helmet>
      {/* Base */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <link rel="canonical" href={pageUrl} />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:locale" content={DEFAULT_SEO.locale} />
      <meta property="og:site_name" content={DEFAULT_SEO.siteName} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={pageUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}

// Données structurées pré-définies
export const STRUCTURED_DATA = {
  localBusiness: {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Axeris Labs",
    "description": "Création de sites web professionnels, e-commerce et applications web sur-mesure",
    "url": "https://www.axeris-labs.fr",
    "logo": "https://www.axeris-labs.fr/favicon.png",
    "email": "contact@axeris-labs.fr",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "61 rue de Lyon",
      "addressLocality": "Paris",
      "postalCode": "75012",
      "addressCountry": "FR"
    },
    "areaServed": [
      { "@type": "City", "name": "Bourg-en-Bresse" },
      { "@type": "City", "name": "Lyon" },
      { "@type": "AdministrativeArea", "name": "Ain" },
      { "@type": "AdministrativeArea", "name": "Savoie" },
      { "@type": "AdministrativeArea", "name": "Haute-Savoie" },
      { "@type": "Country", "name": "Suisse" }
    ],
    "priceRange": "€€",
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "18:00"
    }
  },
  
  service: (serviceName, description, price) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": serviceName,
    "provider": {
      "@type": "ProfessionalService",
      "name": "Axeris Labs"
    },
    "description": description,
    "areaServed": {
      "@type": "Country",
      "name": "France"
    },
    ...(price && { "offers": {
      "@type": "Offer",
      "price": price,
      "priceCurrency": "EUR"
    }})
  }),

  faqPage: (faqs) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  })
};
